// Agency /app/parametres/communications — Lot 16.
// Vue simplifiée : SMS géré par la plateforme (aucun credential visible).
// Uniquement : Email actif · SMS ON/OFF · Quota mensuel.

import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ChevronLeft, Loader2, ShieldCheck, ShieldOff, AlertCircle,
  Mail, MessageSquare, Info,
} from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { AgencyCommsSettingsCard } from "@/pages/platform/CommunicationRouterPanel";

const CommunicationsPage = () => {
  const [loading, setLoading] = useState(true);
  const [settings, setSettings] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async ({ initial = false } = {}) => {
    try {
      if (initial) setLoading(true);
      const s = await api.getCommsSettings();
      setSettings(s);
    } catch (e) { setError(e.message || "Erreur"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load({ initial: true }); }, []);

  const toggleSms = async () => {
    const current = new Set(settings.channels_enabled || []);
    if (current.has("sms")) current.delete("sms");
    else current.add("sms");
    setBusy(true);
    try {
      await api.patchCommsSettings({ channels_enabled: [...current] });
      await load();
    } catch (e) {
      toast.error(e.message || "Erreur");
    } finally { setBusy(false); }
  };

  if (loading) {
    return (
      <div className="mx-auto max-w-4xl px-6 lg:px-10 py-10">
        <Loader2 size={20} className="animate-spin" />
      </div>
    );
  }
  if (!settings) return null;

  const email = settings.email || {};
  const sms = settings.sms || {};
  const quota = settings.quota || {};
  const smsEnabled = (settings.channels_enabled || []).includes("sms");
  const smsAvailable = sms.platform_configured && sms.platform_verified;

  return (
    <div className="mx-auto max-w-4xl px-6 lg:px-10 py-10" data-testid="page-communications">
      <Link to="/app/parametres"
            className="inline-flex items-center gap-1.5 text-[13px] font-medium text-[#6B7280] hover:text-[#111]">
        <ChevronLeft size={14} /> Retour aux paramètres
      </Link>
      <h1 className="mt-4 text-3xl font-semibold tracking-[-0.02em] text-[#111]">Communications</h1>
      <p className="mt-1.5 text-[14px] text-[#6B7280] max-w-2xl">
        Emails et SMS transactionnels de votre agence — gérés par la plateforme wyvio.
        Aucune configuration technique de votre côté.
      </p>

      {/* --- EMAIL --- */}
      <section className="mt-10 rounded-2xl border border-black/5 bg-white p-6" data-testid="section-email">
        <div className="flex items-start gap-4">
          <div className="h-10 w-10 rounded-xl bg-[#F7F8FA] flex items-center justify-center text-[#111]">
            <Mail size={17} />
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <h2 className="text-[16px] font-semibold text-[#111]">Email</h2>
              <ActivePill on />
            </div>
            <p className="mt-1 text-[13px] text-[#6B7280]">
              Expéditeur : <b>{email.from_name}</b> · Adresse : <b>{email.from_email}</b>
            </p>
            <p className="mt-1 text-[11.5px] text-[#9CA3AF]">
              Les emails transactionnels (invitation, documents, paiements, livraison…)
              sont envoyés automatiquement selon la configuration de vos workflows.
            </p>
          </div>
        </div>
      </section>

      {/* --- SMS --- */}
      <section className="mt-6 rounded-2xl border border-black/5 bg-white p-6" data-testid="section-sms">
        <div className="flex items-start gap-4">
          <div className="h-10 w-10 rounded-xl bg-[#F7F8FA] flex items-center justify-center text-[#111]">
            <MessageSquare size={17} />
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <h2 className="text-[16px] font-semibold text-[#111]">SMS transactionnels</h2>
              <ActivePill on={smsEnabled && smsAvailable} />
              {sms.sender && (
                <span className="text-[11.5px] text-[#6B7280] ml-1">
                  Expéditeur : <b>{sms.sender}</b>
                </span>
              )}
            </div>
            <p className="mt-1 text-[13px] text-[#6B7280] leading-relaxed">
              Les SMS importants (invitation, magic link, document disponible,
              paiement, livraison, CPI) sont envoyés automatiquement lorsque le canal
              est activé.
            </p>

            {!smsAvailable && (
              <div className="mt-4 rounded-lg bg-[#FEF3C7] text-[#92400E] px-3 py-2.5 flex items-start gap-2 text-[12.5px]"
                   data-testid="sms-platform-not-ready">
                <AlertCircle size={14} className="mt-0.5 shrink-0" />
                Le service SMS de la plateforme n'est pas encore opérationnel.
                Contactez le support si le problème persiste.
              </div>
            )}

            <div className="mt-5">
              <button onClick={toggleSms}
                      disabled={busy || (!smsAvailable && !smsEnabled)}
                      data-testid="sms-toggle-btn"
                      className={`h-10 inline-flex items-center gap-2 rounded-full text-[13px] font-medium px-5 disabled:opacity-40
                        ${smsEnabled ? "bg-[#FEE2E2] text-[#991B1B]" : "bg-[#D9FFE8] text-[#166534]"}`}>
                {busy ? <Loader2 size={14} className="animate-spin" /> :
                  smsEnabled ? "Désactiver les SMS transactionnels" : "Activer les SMS transactionnels"}
              </button>
            </div>
          </div>
        </div>

        {/* --- Quota (mois courant) --- */}
        <div className="mt-6 pt-6 border-t border-black/5" data-testid="sms-quota">
          <p className="text-[11.5px] font-semibold uppercase tracking-widest text-[#6B7280]">
            Consommation SMS · {quota.period}
          </p>
          <div className="mt-2 flex items-baseline gap-3">
            <span className="text-[26px] font-semibold text-[#111]">{quota.used_sms || 0}</span>
            <span className="text-[13px] text-[#6B7280]">
              / {quota.included_sms + (quota.extra_sms || 0)} SMS inclus
            </span>
          </div>
          <div className="mt-3 h-2 w-full bg-[#F1F5F9] rounded-full overflow-hidden">
            <div
              className={`h-full rounded-full ${quota.over_quota ? "bg-[#EF4444]" : "bg-[#8FB600]"}`}
              style={{
                width: `${Math.min(100, ((quota.used_sms || 0) /
                        Math.max(1, (quota.included_sms + (quota.extra_sms || 0)))) * 100)}%`
              }}
            />
          </div>
          <p className="mt-2 text-[12px] text-[#6B7280]">
            Restant : <b className="text-[#111]">{quota.remaining_sms}</b> SMS
            {quota.extra_sms > 0 && ` · ${quota.extra_sms} SMS supplémentaires accordés`}
          </p>
        </div>
      </section>

      {/* Lot 19 — Déclencheurs SMS granulaires */}
      <section className="mt-6">
        <AgencyCommsSettingsCard />
      </section>
    </div>
  );
};

const ActivePill = ({ on }) => (
  <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold
    ${on ? "bg-[#D9FFE8] text-[#166534]" : "bg-[#F1F5F9] text-[#475569]"}`}>
    {on ? <><ShieldCheck size={11} /> Actif</> : <><ShieldOff size={11} /> Inactif</>}
  </span>
);

export default CommunicationsPage;
